import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import {
  useStockHistory, useUpdateStockItem, useArchiveStockItem, useRestoreStockItem,
  useUploadStockPhoto, useReverseStockEntry
} from '../../hooks/useStock';
import { formatCount, formatSignedCount, parseWholeNumber } from '../../utils/numberFormatter';
import { formatShortDateTime } from '../../utils/dateFormatter';
import { TASK_PHOTO_MAX_BYTES, TASK_PHOTO_MIME_TYPES } from '../../config/taskPhotoRules';
import { TEXT, UNIT_LABELS, KIND_LABELS, errorText, itemNames } from '../../config/stockLabels';
import { tileState } from './StockItemTile';

/**
 * One item: its photo, names and current count, an edit form, its full history with «تراجع», and
 * archive / restore (tasks/stock.md D9, D14, D16).
 *
 * The history is the record (D9) — nothing here edits an entry. «تراجع» adds a reversal; when a later
 * count already covered the row, the server records 0 and says so (`countUnchanged`), and that is said
 * here in words.
 */
const REVERSIBLE = ['opening', 'purchase', 'take_out'];

/** The signed change of one entry, as a reader expects it: «+٢٤», «−٣٠», «٠». */
const SignedChange = ({ n }) => (
  <bdi dir="ltr" className={`stk-hist__qty ${n > 0 ? 'is-up' : n < 0 ? 'is-down' : ''}`}>
    {n > 0 ? '+' : n < 0 ? '−' : ''}{formatCount(Math.abs(n))}
  </bdi>
);

const HistoryRow = ({ entry, unitLabel, onReverse, reversing }) => {
  const canReverse = REVERSIBLE.includes(entry.kind) && !entry.reversed;
  return (
    <li className={`stk-hist__row${entry.reversed ? ' is-reversed' : ''}`}>
      <div className="stk-hist__main">
        <span className="stk-hist__kind">{KIND_LABELS[entry.kind] || entry.kind}</span>
        <SignedChange n={entry.quantity} />
        <span className="stk-hist__balance">{TEXT.balanceAfter}: <bdi dir="ltr">{formatSignedCount(entry.balanceAfter)}</bdi> {unitLabel}</span>
      </div>
      <div className="stk-hist__meta">
        {entry.kind === 'recount' && <span>{TEXT.counted}: {formatCount(entry.countedQuantity)}</span>}
        {entry.takenBy && <span>{TEXT.takenBy}: {entry.takenBy.name || TEXT.unnamed}</span>}
        {entry.recordedBy && <span>{TEXT.recordedBy}: {entry.recordedBy.name || TEXT.unnamed}</span>}
        <span>{formatShortDateTime(entry.at)}</span>
      </div>
      {entry.note && <p className="stk-hist__note">{entry.note}</p>}
      {entry.reversed && <span className="stk-hist__tag">{TEXT.reversed}</span>}
      {canReverse && (
        <Button variant="link" size="sm" className="stk-link stk-hist__undo" onClick={() => onReverse(entry)} disabled={reversing}>
          <i className="bi bi-arrow-counterclockwise" aria-hidden="true"></i> {TEXT.reverse}
        </Button>
      )}
    </li>
  );
};

const StockItemModal = ({ show, onHide, storeId, item }) => {
  const itemId = item?.id || null;
  const { data: history, isPending: historyPending, isError: historyError, refetch } = useStockHistory(show ? storeId : null, show ? itemId : null);
  const updateItem = useUpdateStockItem();
  const archiveItem = useArchiveStockItem();
  const restoreItem = useRestoreStockItem();
  const uploadPhoto = useUploadStockPhoto();
  const reverseEntry = useReverseStockEntry();
  const fileRef = useRef(null);
  const reverseRef = useRef(null);

  const [editing, setEditing] = useState(false);
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [level, setLevel] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reversingEntry, setReversingEntry] = useState(null);
  const [reverseNote, setReverseNote] = useState('');

  // Every opening starts clean.
  useEffect(() => {
    if (!show || !item) return;
    setEditing(false);
    setNameAr(item.nameAr || '');
    setNameEn(item.nameEn || '');
    setLevel(item.lowStockLevel === null || item.lowStockLevel === undefined ? '' : String(item.lowStockLevel));
    setError(null);
    setNotice(null);
    setReversingEntry(null);
    setReverseNote('');
  }, [show, itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The confirmation sits above the history: an undo clicked far down a long history must not open it
  // out of sight. Smooth only when the reader has not asked for reduced motion.
  useEffect(() => {
    if (!reversingEntry || !reverseRef.current) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    reverseRef.current.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }, [reversingEntry]);

  if (!item) return null;
  const { primary, secondary } = itemNames(item);
  const unitLabel = UNIT_LABELS[item.unit] || item.unit;
  const state = tileState(item);
  const busy = updateItem.isPending || archiveItem.isPending || restoreItem.isPending || uploadPhoto.isPending || reverseEntry.isPending;

  const run = async (fn) => {
    setError(null);
    setNotice(null);
    try {
      return await fn();
    } catch (err) {
      setError(errorText(err));
      return null;
    }
  };

  const saveEdit = async () => {
    const trimmed = level.trim();
    const lowStockLevel = trimmed === '' ? null : parseWholeNumber(trimmed);
    if (trimmed !== '' && lowStockLevel === null) { setError(TEXT.wholeNumber); return; }
    const done = await run(() => updateItem.mutateAsync({ itemId, changes: { nameAr, nameEn, lowStockLevel } }));
    if (done) setEditing(false);
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!TASK_PHOTO_MIME_TYPES.includes(file.type)) { setError(errorText({ response: { data: { code: 'STOCK_024' } } })); return; }
    if (file.size > TASK_PHOTO_MAX_BYTES) { setError(errorText({ response: { data: { code: 'STOCK_023' } } })); return; }
    await run(() => uploadPhoto.mutateAsync({ itemId, file }));
  };

  const confirmReverse = async () => {
    const res = await run(() => reverseEntry.mutateAsync({ entryId: reversingEntry.id, note: reverseNote.trim() || undefined }));
    if (res) {
      setReversingEntry(null);
      setReverseNote('');
      if (res.countUnchanged) setNotice(TEXT.reverseCountUnchanged);
      refetch();
    }
  };

  const toggleArchive = async () => {
    const mutation = item.archived ? restoreItem : archiveItem;
    const res = await run(() => mutation.mutateAsync({ itemId }));
    if (res) onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable fullscreen="sm-down" size="lg" dir="rtl" dialogClassName="stk-modal">
      <Modal.Header closeButton>
        <Modal.Title className="stk-modal__title">{primary}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className={`stk-item stk-item--${state}`}>
          <div className="stk-item__photo">
            {item.photoUrl
              ? <img src={item.photoUrl} alt={primary} />
              : <span className="stk-item__nophoto"><i className="bi bi-image" aria-hidden="true"></i>{TEXT.noPhoto}</span>}
            <input ref={fileRef} type="file" accept={TASK_PHOTO_MIME_TYPES.join(',')} hidden onChange={pickPhoto} />
            <Button variant="outline-primary" size="sm" onClick={() => fileRef.current?.click()} disabled={busy}>
              {uploadPhoto.isPending
                ? <><Spinner animation="border" size="sm" className="stk-spin" />{TEXT.uploadingPhoto}</>
                : <><i className="bi bi-camera" aria-hidden="true"></i> {item.photoUrl ? TEXT.changePhoto : TEXT.choosePhoto}</>}
            </Button>
          </div>
          <div className="stk-item__facts">
            <p className="stk-item__name-en" dir="ltr">{secondary}</p>
            <p className="stk-item__label">{TEXT.itemCurrent}</p>
            <p className="stk-item__count">
              <bdi dir="ltr" className="stk-item__num">{formatSignedCount(item.quantity)}</bdi>
              <span className="stk-item__unit">{unitLabel}</span>
            </p>
            {item.lowStockLevel !== null && item.lowStockLevel !== undefined && (
              <p className="stk-muted">{TEXT.lowLevel}: {formatCount(item.lowStockLevel)} {unitLabel}</p>
            )}
            {!editing && !item.archived && (
              <Button variant="link" className="stk-link" onClick={() => setEditing(true)}>
                <i className="bi bi-pencil" aria-hidden="true"></i> {TEXT.editItem}
              </Button>
            )}
          </div>
        </section>

        {editing && (
          <section className="stk-edit">
            <Form.Group className="stk-field" controlId="stk-edit-ar">
              <Form.Label>{TEXT.nameAr}</Form.Label>
              <Form.Control value={nameAr} maxLength={80} onChange={(e) => setNameAr(e.target.value)} />
            </Form.Group>
            <Form.Group className="stk-field" controlId="stk-edit-en">
              <Form.Label>{TEXT.nameEn}</Form.Label>
              <Form.Control value={nameEn} maxLength={80} dir="ltr" onChange={(e) => setNameEn(e.target.value)} />
            </Form.Group>
            <Form.Group className="stk-field" controlId="stk-edit-level">
              <Form.Label>{TEXT.lowStockLevel}</Form.Label>
              <Form.Control value={level} inputMode="numeric" autoComplete="off" onChange={(e) => setLevel(e.target.value)} />
              <Form.Text>{TEXT.lowStockLevelHint}</Form.Text>
            </Form.Group>
            <p className="stk-muted">{TEXT.unit}: {unitLabel} — {TEXT.unitLocked}</p>
            <div className="stk-edit__buttons">
              <Button variant="outline-secondary" onClick={() => setEditing(false)} disabled={busy}>{TEXT.cancel}</Button>
              <Button variant="primary" onClick={saveEdit} disabled={busy}>
                {updateItem.isPending && <Spinner animation="border" size="sm" className="stk-spin" />}
                {TEXT.save}
              </Button>
            </div>
          </section>
        )}

        {error && <div className="stk-error" role="alert">{error}</div>}
        {notice && <div className="stk-notice" role="status">{notice}</div>}

        {reversingEntry && (
          <section className="stk-reverse" aria-live="polite" ref={reverseRef}>
            <h4 className="stk-subtitle">{TEXT.reverseTitle}</h4>
            <p className="stk-hist__meta">
              <span>{KIND_LABELS[reversingEntry.kind]}</span>
              <SignedChange n={reversingEntry.quantity} />
              <span>{formatShortDateTime(reversingEntry.at)}</span>
            </p>
            <p className="stk-muted">{TEXT.reverseHint}</p>
            <Form.Group className="stk-field" controlId="stk-reverse-note">
              <Form.Label>{TEXT.reverseNote}</Form.Label>
              <Form.Control value={reverseNote} maxLength={500} onChange={(e) => setReverseNote(e.target.value)} />
            </Form.Group>
            <div className="stk-edit__buttons">
              <Button variant="outline-secondary" onClick={() => setReversingEntry(null)} disabled={busy}>{TEXT.cancel}</Button>
              <Button variant="danger" onClick={confirmReverse} disabled={busy}>
                {reverseEntry.isPending && <Spinner animation="border" size="sm" className="stk-spin" />}
                {TEXT.reverseConfirm}
              </Button>
            </div>
          </section>
        )}

        <section className="stk-hist">
          <h4 className="stk-subtitle">{TEXT.history}</h4>
          {historyPending && !historyError && <div className="stk-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>}
          {historyError && (
            <div className="stk-error" role="alert">
              {TEXT.loadError} <Button variant="link" className="stk-link" onClick={() => refetch()}>{TEXT.refresh}</Button>
            </div>
          )}
          {history && history.entries.length === 0 && <p className="stk-muted">{TEXT.historyEmpty}</p>}
          {history && history.entries.length > 0 && (
            <>
              <ul className="stk-hist__list">
                {history.entries.map(entry => (
                  <HistoryRow key={entry.id} entry={entry} unitLabel={unitLabel} reversing={busy}
                    onReverse={(e) => { setReversingEntry(e); setReverseNote(''); setError(null); setNotice(null); }} />
                ))}
              </ul>
              {history.total > history.entries.length && (
                <p className="stk-muted">{TEXT.showingLatest(history.entries.length, history.total)}</p>
              )}
            </>
          )}
        </section>
      </Modal.Body>
      <Modal.Footer className="stk-item__foot">
        <div>
          <Button variant={item.archived ? 'outline-primary' : 'outline-danger'} size="sm" onClick={toggleArchive} disabled={busy}>
            {item.archived ? TEXT.restore : TEXT.archive}
          </Button>
          {!item.archived && <p className="stk-muted stk-item__archive-hint">{TEXT.archiveHint}</p>}
        </div>
        <Button variant="primary" onClick={onHide}>{TEXT.close}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StockItemModal;
